import {
  CameraOutlined,
  LockOutlined,
  LogoutOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Gender } from "@axl/membership-sdk";
import { Input, Button, Spin, Select, Form, Alert } from "antd";
import { Rule } from "antd/es/form";
import moment, { type Moment } from "moment";
import { useState, useCallback, useRef, useMemo } from "react";

import {
  useAuth,
  useLoc,
  useNotifier,
  useSchool,
  useSdk,
} from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { DateSplitPicker } from "@app/shared/ui/DateSplitPicker";
import { FormBuilder } from "@app/shared/ui/FormBuilder";
import { ImagePicker } from "@app/shared/ui/ImagePicker/ImagePicker";
import { UserAvatar } from "@app/shared/ui/UserAvatar";
import {
  PhoneInput,
  validatePhoneNumber,
} from "@app/shared/ui/PhoneInput2/PhoneInput";

import styles from "./CommonSettingsForm.module.scss";

type Props = {
  viewMode: "default" | "required";
  onSave?: () => void;
};

type StudentProfileState = {
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  password?: string;
  confirmPassword?: string;
  gender?: Gender;
  birthday?: Moment;
  avatarId?: string;
};

export const CommonSettingsForm: React.FC<Props> = ({ viewMode, onSave }) => {
  const loc = useLoc();
  const user = useUser().user!;
  const sdk = useSdk();
  const { logout } = useAuth();
  const [form] = Form.useForm();
  const school = useSchool();
  const profileSettings = useMemo(
    () => school.profileSettings,
    [school.profileSettings],
  );
  const avatarUploadingRef = useRef(false);
  const [changingPassword, setChangingPassword] = useState(
    user.needChangePassword,
  );

  const getInitialValues = () => {
    return {
      birthday: user.birthday ? moment(user.birthday).utc() : undefined,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      gender: user.gender ?? undefined,
      middleName: user.middleName,
      phone: user.phone,
      avatarId: user.avatar?.id,
    };
  };

  const [state, setState] = useState<StudentProfileState>(getInitialValues());
  const [avatar, setAvatar] = useState(user.avatar);
  const [saving, setSaving] = useState(false);
  const [notifier] = useNotifier();

  const firstNameRules: Rule[] = [
    {
      required: profileSettings.firstNameRequired,
      message: loc.word("Required field"),
    },
  ];

  const lastNameRules: Rule[] = [
    {
      required: profileSettings.lastNameRequired,
      message: loc.word("Required field"),
    },
  ];

  const middleNameRules: Rule[] = [
    {
      required: profileSettings.middleNameRequired,
      message: loc.word("Required field"),
    },
  ];

  const phoneRules: Rule[] = [
    {
      required: profileSettings.phoneRequired,
      validator: async (_rule: unknown, value: string) => {
        const errorMessage = validatePhoneNumber(
          value,
          profileSettings.phoneRequired,
          {
            required: loc.word("Required field"),
            invalid: loc.word("Enter valid phone number"),
          },
        );

        if (errorMessage) throw new Error(errorMessage);
      },
    },
  ];

  const birthdayRules: Rule[] = [
    {
      required: profileSettings.birthdayRequired,
      message: loc.word("Required field"),
    },
  ];

  const genderRules: Rule[] = [
    {
      required: profileSettings.genderRequired,
      message: loc.word("Required field"),
    },
  ];

  const passwordRules: Rule[] = [
    {
      required: user.needChangePassword,
      message: loc.word("Required field"),
    },
    {
      type: "string",
      min: 6,
      message: loc.word("Password must be longer than 6 characters"),
    },
    {
      type: "string",
      max: 30,
      message: loc.word("Password must be shorter than 30 characters"),
    },
  ];

  const passwordConfirmRules: Rule[] = [
    {
      validator: async (_rule, value) => {
        if (state.password !== value)
          throw new Error(loc.word("Your passwords must match"));
      },
    },
  ];

  const save = useCallback(async () => {
    setSaving(true);
    const result = await sdk.api.account.save(state);
    if (result.success) onSave?.();
    notifier(result, loc.word("Profile has been saved"));
    setSaving(false);
  }, [onSave, state]);

  return (
    <>
      {viewMode === "required" && (
        <Alert
          type="warning"
          icon={<InfoCircleOutlined />}
          showIcon
          title={loc.word("StudentProfileForm.help", {
            default:
              "Before using the platform, you must fill in the required fields of your profile",
          })}
          className="mb-10"
        />
      )}

      <Spin description={loc.word("Loading")} spinning={saving}>
        <Form
          form={form}
          onFinish={() => save()}
          initialValues={getInitialValues()}
          layout="vertical"
        >
          {/* who: the photo (click to change) with the name and email beside it */}
          <div className={styles.head}>
            <FormBuilder.Custom
              name="avatar"
              noStyle
              rules={(x) =>
                x.custom({
                  async validator() {
                    if (avatarUploadingRef.current)
                      throw new Error(
                        loc.word("Student.avatar.uploadingInProgress", {
                          default: "Avatar is uploading",
                        }),
                      );
                  },
                })
              }
            >
              <ImagePicker
                srcFile={avatar}
                className={styles.avatarPicker}
                placeholder={
                  <span className={styles.avatarPlaceholder}>
                    <UserAvatar user={user} size={88} color="var(--axl-primary)" />
                    <span className={styles.avatarCamera}>
                      <CameraOutlined />
                    </span>
                  </span>
                }
                onChange={(file) => {
                  setState({ ...state, avatarId: file?.id });
                  setAvatar(file ?? null);
                }}
                onUploadingChange={(x) => {
                  avatarUploadingRef.current = x;
                }}
              />
            </FormBuilder.Custom>
            <div className={styles.headText}>
              <div className={styles.headName}>{user.fullName || user.email}</div>
              <div className={styles.headEmail}>{user.email}</div>
            </div>
          </div>


          <div className={styles.grid}>
          <Form.Item name="email" label={loc.word("Email")} className={styles.full}>
            <Input
              disabled={profileSettings.blockEmailChange}
              onChange={(e) => setState({ ...state, email: e.target.value })}
            />
          </Form.Item>

          {(viewMode === "default" || profileSettings.firstNameRequired) && (
            <Form.Item
              name="firstName"
              rules={firstNameRules}
              label={loc.word("First name")}
            >
              <Input
                placeholder={loc.word("Enter first name")}
                onChange={(e) =>
                  setState({ ...state, firstName: e.target.value })
                }
              />
            </Form.Item>
          )}

          {(viewMode === "default" || profileSettings.lastNameRequired) && (
            <Form.Item
              name="lastName"
              rules={lastNameRules}
              label={loc.word("Last name")}
            >
              <Input
                placeholder={loc.word("Enter last name")}
                onChange={(e) =>
                  setState({ ...state, lastName: e.target.value })
                }
              />
            </Form.Item>
          )}

          {(viewMode === "default" || profileSettings.middleNameRequired) && (
            <Form.Item
              name="middleName"
              rules={middleNameRules}
              label={loc.word("Middle name")}
            >
              <Input
                placeholder={loc.word("Enter middle name")}
                onChange={(e) =>
                  setState({ ...state, middleName: e.target.value })
                }
              />
            </Form.Item>
          )}

          {(viewMode === "default" || profileSettings.phoneRequired) && (
            <Form.Item
              name="phone"
              rules={phoneRules}
              label={loc.word("Phone number")}
            >
              <PhoneInput
                placeholder={loc.word("Enter phone number")}
                onChange={(phone) => setState({ ...state, phone })}
              />
            </Form.Item>
          )}

          {(viewMode === "default" || profileSettings.birthdayRequired) && (
            <Form.Item
              name="birthday"
              label={loc.word("Birthday")}
              rules={birthdayRules}
            >
              <DateSplitPicker
                onChange={(date) => {
                  const bd = moment(date).utc(true);
                  setState({ ...state, birthday: bd });
                }}
              />
            </Form.Item>
          )}

          {(viewMode === "default" || profileSettings.genderRequired) && (
            <Form.Item
              name="gender"
              rules={genderRules}
              label={loc.word("Gender")}
            >
              <Select
                placeholder={loc.word("Gender")}
                onChange={(gender: Gender) => setState({ ...state, gender })}
              >
                <Select.Option value="male">{loc.word("male")}</Select.Option>
                <Select.Option value="female">
                  {loc.word("female")}
                </Select.Option>
              </Select>
            </Form.Item>
          )}
          </div>

          {viewMode === "default" && (
            <>
              {changingPassword ? (
                <div className={styles.grid}>
                  <Form.Item
                    label={loc.word("Password")}
                    name="password"
                    rules={passwordRules}
                  >
                    <Input.Password
                      type="password"
                      placeholder={loc.word("Enter your password")}
                      onChange={(e) =>
                        setState({ ...state, password: e.target.value })
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    label={loc.word("Confirm password")}
                    name="confirmPassword"
                    rules={passwordConfirmRules}
                  >
                    <Input.Password
                      type="password"
                      placeholder={loc.word("Enter password confirmation")}
                      onChange={(e) =>
                        setState({ ...state, confirmPassword: e.target.value })
                      }
                    />
                  </Form.Item>
                </div>
              ) : (
                <Button
                  type="link"
                  className={styles.passwordLink}
                  onClick={() => setChangingPassword(true)}
                  icon={<LockOutlined />}
                  loading={saving}
                >
                  {loc.word("Change password", { default: "Change password" })}
                </Button>
              )}
            </>
          )}
        </Form>
      </Spin>
      <div className={styles.footer}>
        {viewMode === "required" ? (
          <Button type="text" danger icon={<LogoutOutlined />} onClick={() => logout()}>
            {loc.word("Logout")}
          </Button>
        ) : (
          <span />
        )}
        <Button type="primary" size="large" onClick={() => form.submit()} loading={saving}>
          {loc.word("Save")}
        </Button>
      </div>
    </>
  );
};
